/**
 * Tasks/notes panel that lives on the Furniture Project Detail page.
 *
 * Mirrors what the admin Staff Tasks page and the staff "My tasks" page
 * already show, but filtered to a single project so the people working
 * inside that project's scope can see — and reply to — anything tagged
 * to it without context-switching.
 *
 * Behavior depends on which user surface the project page is rendered in:
 *
 *  • Admin (no isStaffMode / isPublicAccess): pulls via
 *    `adminListTasks(pw, { project_id })`. Clicking a row opens the same
 *    TaskDrawer used by /furniture-projects/tasks so messaging works.
 *
 *  • Staff (isStaffMode): pulls via `meTasksForProject(projectId)` —
 *    server already enforces "only your own tasks" for non-admins. Opens
 *    the StaffTaskSheet for mark-done / reply.
 *
 *  • Share / View tokens (isPublicAccess): the parent doesn't even
 *    mount this component (tasks are internal).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Loader2, CheckCircle2, Circle, MessageSquare, Calendar, Plus, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  adminListTasks, adminMarkTaskRead, meTasksForProject, meMarkTaskRead, fmtErr,
} from '@/lib/staffApi';
import { TaskDrawer } from '@/pages/AdminStaffTasksPage';
import { StaffTaskSheet } from '@/pages/staff/StaffTasksPage';

const PRIORITY_LABEL = {
  1: 'P1 · ASAP', 2: 'P2 · Deadline', 3: 'P3 · Date',
  4: 'P4 · Window', 5: 'P5 · When free',
};
const PRIORITY_TONE = {
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-amber-100 text-amber-800 border-amber-200',
  4: 'bg-sky-100 text-sky-800 border-sky-200',
  5: 'bg-muted text-muted-foreground border-border',
};

function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}
function fmtDate(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function dateLine(t) {
  if (t.priority === 2 && t.due_at) return `Due ${fmtDateTime(t.due_at)}`;
  if (t.priority === 3 && t.due_date) return `Do on ${fmtDate(t.due_date)}`;
  if (t.priority === 4 && (t.start_date || t.end_date)) {
    return `${fmtDate(t.start_date)} – ${fmtDate(t.end_date)}`;
  }
  return '';
}

export function ProjectTasksPanel({ projectId, isStaffMode = false }) {
  const navigate = useNavigate();
  const adminPass = !isStaffMode && typeof window !== 'undefined'
    ? sessionStorage.getItem('app_password') || ''
    : '';

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState({ open: [], done: [] });
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (isStaffMode) {
        const r = await meTasksForProject(projectId);
        // /by-project returns a flat list — bucket here.
        const open = (r.items || []).filter(t => t.status !== 'done');
        const done = (r.items || []).filter(t => t.status === 'done');
        setTasks({ open, done });
      } else {
        // Admin endpoint returns { open, done } already filtered + sorted.
        const r = await adminListTasks(adminPass, { project_id: projectId });
        setTasks(r);
      }
    } catch (e) { toast.error(fmtErr(e, 'Failed to load tasks')); }
    finally { setLoading(false); }
  }, [projectId, isStaffMode, adminPass]);

  useEffect(() => { refresh(); }, [refresh]);

  // Tap a task to open the right surface (drawer for admin, sheet for staff)
  // and silently mark it read on the side that's viewing it.
  const handleSelect = async (task) => {
    setSelected(task);
    if (isStaffMode && task.unread_for_assignee) {
      try { await meMarkTaskRead(task.id); refresh(); } catch {}
    } else if (!isStaffMode && task.unread_for_admin) {
      try { await adminMarkTaskRead(adminPass, task.id); refresh(); } catch {}
    }
  };

  const newTaskHref = isStaffMode
    ? null
    : `/furniture-projects/tasks?project=${projectId}`;

  return (
    <div className="space-y-4" data-testid="project-tasks-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold text-[#1f2933]">Tasks &amp; notes</h3>
          <p className="text-xs text-[#5b6773] mt-0.5">
            {isStaffMode
              ? 'Tasks the admin has tagged to this project for you.'
              : 'Every staff task tagged to this project. Click to reply or mark read.'}
          </p>
        </div>
        {newTaskHref && (
          <Button size="sm" variant="outline" onClick={() => navigate(newTaskHref)} data-testid="project-tasks-new-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New task
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : tasks.open.length === 0 && tasks.done.length === 0 ? (
        <Card className="border-dashed border-2 bg-transparent">
          <div className="py-10 text-center text-sm text-[#5b6773]">
            No tasks tagged to this project yet.
            {!isStaffMode && newTaskHref && (
              <> <button onClick={() => navigate(newTaskHref)} className="text-emerald-700 hover:underline">Create one →</button></>
            )}
          </div>
        </Card>
      ) : (
        <>
          {tasks.open.length > 0 && (
            <section data-testid="project-tasks-open">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
                Open ({tasks.open.length})
              </p>
              <div className="space-y-2">
                {tasks.open.map(t => (
                  <TaskRow key={t.id} task={t} isStaff={isStaffMode} onSelect={handleSelect} />
                ))}
              </div>
            </section>
          )}
          {tasks.done.length > 0 && (
            <section data-testid="project-tasks-done">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2 mt-4">
                Done ({tasks.done.length})
              </p>
              <div className="space-y-2 opacity-75">
                {tasks.done.map(t => (
                  <TaskRow key={t.id} task={t} isStaff={isStaffMode} onSelect={handleSelect} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Detail surfaces — re-using the same components the dedicated pages use */}
      {!isStaffMode && (
        <TaskDrawer
          task={selected}
          onClose={() => setSelected(null)}
          adminPass={adminPass}
          isAdmin
          onRefresh={refresh}
        />
      )}
      {isStaffMode && (
        <StaffTaskSheet
          task={selected}
          onClose={() => setSelected(null)}
          onRefresh={refresh}
        />
      )}

      {!isStaffMode && (
        <p className="pt-2 text-[11px] text-muted-foreground">
          <ExternalLink className="h-3 w-3 inline-block mr-1 -mt-0.5" />
          Manage all staff tasks at <button onClick={() => navigate('/furniture-projects/tasks')} className="text-emerald-700 hover:underline">Staff Tasks</button>.
        </p>
      )}
    </div>
  );
}

function TaskRow({ task, isStaff, onSelect }) {
  const dl = dateLine(task);
  const unread = isStaff ? task.unread_for_assignee : task.unread_for_admin;
  return (
    <Card
      className={`cursor-pointer hover:shadow-sm transition-shadow ${unread ? 'ring-1 ring-emerald-300' : ''}`}
      onClick={() => onSelect(task)}
      data-testid={`project-task-row-${task.id}`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        {task.status === 'done'
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          : <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${task.status === 'done' ? 'line-through text-muted-foreground' : 'text-[#1f2933]'}`}>
            {task.title}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <Badge className={`${PRIORITY_TONE[task.priority]} text-[10px]`}>{PRIORITY_LABEL[task.priority]}</Badge>
            {task.assignee_name && <span className="text-[11px] text-muted-foreground">{task.assignee_name}</span>}
            {dl && (
              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                <Calendar className="h-2.5 w-2.5" /> {dl}
              </span>
            )}
          </div>
        </div>
        {(task.thread?.length > 0) && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {task.thread.length}
          </span>
        )}
        {unread && <span className="h-2 w-2 bg-emerald-500 rounded-full" title="Unread" />}
      </div>
    </Card>
  );
}
